import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Modal } from 'antd';
import Axios from 'axios';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useProject } from '@hooks/API/Project/UseProject';

import Switch from '@components/Gui/CustomSwitch';
import { Loading } from '@components/Gui/Loading';
import ModelsConfirmedInfo from '@components/Gui/ModelsConfirmedInfo';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import SelectionBar from '@components/Gui/SelectionBar';
import PdfPrintDrawer from '@components/PdfPrintDrawer/PdfPrintDrawer';
import { ModelSelectorContainer } from '@components/Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import {
    getProjectModelsWithEventDates,
    isEventActive,
    sortProjectModelsByConfirmation,
} from '@components/Project/ProjectUtils';
import ButtonTemplateSelector from '@components/TemplateSelector/ButtonTemplateSelector';

import ProjectPrintEventItem from './ProjectPrintEventItem';
import { ReportCreationDrawerWrapper } from './ReportCreationDrawerUI';

const PRINT_AREA = 'ProjectModelsList';
const OUTPUT_FILE_NAME = 'TalentsReport';

const buildProjectModels = (project) => {
    const models = getProjectModelsWithEventDates(project, getContactName);

    return sortProjectModelsByConfirmation(
        models.map((model) => {
            const activeJobs = model.EventDates.filter((eventDate) => isEventActive(eventDate));
            const alwaysCancelled =
                model.EventDates.length > 0 && model.EventDates.every((eventDate) => eventDate.Cancel);

            return {
                ...model,
                Active: activeJobs.length > 0,
                AlwaysCancelled: alwaysCancelled,
                HasConfirmation: activeJobs.length > 0,
            };
        })
    );
};

const getVisibleModels = (models, activeEventIds) => {
    if (activeEventIds.length === 0) {
        return [];
    }

    const activeEventIdSet = new Set(activeEventIds);
    const visibleModels = models
        .map((model) => {
            const eventDates = model.EventDates.filter((eventDate) => activeEventIdSet.has(eventDate.EventID));

            if (eventDates.length === 0) {
                return null;
            }

            const activeJobs = eventDates.filter((eventDate) => isEventActive(eventDate));
            const alwaysCancelled = eventDates.every((eventDate) => eventDate.Cancel);

            return {
                ...model,
                EventDates: eventDates,
                AlwaysCancelled: alwaysCancelled,
                HasConfirmation: activeJobs.length > 0,
            };
        })
        .filter((model) => model !== null);

    return sortProjectModelsByConfirmation(visibleModels);
};

const ModelName = ({ model }) => {
    let classNames = 'model-name';

    if (model.AlwaysCancelled) {
        classNames += ' is-cancelled';
    }
    if (model.HasConfirmation) {
        classNames += ' is-confirmed';
    }

    return <div className={classNames}>{model.ModelName}</div>;
};

const ProjectModelsListDrawer = ({ projectId, onClose }) => {
    const { data: currentProject, isFetching: isFetchingProject } = useProject(projectId);
    const [projectEvents, setProjectEvents] = useState([]);
    const [models, setModels] = useState([]);
    const [docVersion, setDocVersion] = useState(null);
    const [isFillingTemplate, setIsFillingTemplate] = useState(false);

    useEffect(() => {
        if (currentProject) {
            setProjectEvents(
                (currentProject.Events || []).map((event) => ({
                    ...event,
                    active: true,
                }))
            );
            setModels(buildProjectModels(currentProject));
        }
    }, [currentProject]);

    const activeEventIds = useMemo(() => {
        return projectEvents.filter((event) => event.active).map((event) => event.ID);
    }, [projectEvents]);

    const visibleModels = useMemo(() => {
        return getVisibleModels(models, activeEventIds);
    }, [models, activeEventIds]);

    const selectedModelIds = useMemo(() => {
        return visibleModels.filter((model) => model.Active).map((model) => model.ModelID);
    }, [visibleModels]);

    const handleChangeEvent = (eventIndex) => (active) => {
        const newProjectEvents = [...projectEvents];
        newProjectEvents[eventIndex].active = active.target.checked;
        setProjectEvents(newProjectEvents);
    };

    const handleChangeModel = (modelID) => (active) => {
        const newModels = models.map((model) => {
            if (model.ModelID !== modelID) {
                return model;
            }
            return {
                ...model,
                Active: active,
            };
        });
        setModels(newModels);
    };

    const updateVisibleModelSelection = (getActive) => {
        const visibleModelsById = new Map(visibleModels.map((model) => [model.ModelID, model]));

        const newModels = models.map((model) => {
            const visibleModel = visibleModelsById.get(model.ModelID);

            if (!visibleModel) {
                return model;
            }

            return {
                ...model,
                Active: getActive(visibleModel),
            };
        });

        setModels(newModels);
    };

    const handleSelectConfirmed = () => {
        updateVisibleModelSelection((model) => model.HasConfirmation);
    };

    const handleSelectAll = () => {
        updateVisibleModelSelection(() => true);
    };

    const handleDeselectAll = () => {
        updateVisibleModelSelection(() => false);
    };

    const modelSelectionActions = [
        { label: 'select confirmed', method: handleSelectConfirmed },
        { label: 'select all', method: handleSelectAll },
        { label: 'deselect all', method: handleDeselectAll },
    ];

    const handleGenerateModelsList = async (templateID) => {
        if (selectedModelIds.length === 0) {
            return;
        }

        setIsFillingTemplate(true);

        const data = {
            Template: templateID,
            Area: PRINT_AREA,
            OutputFileName: OUTPUT_FILE_NAME,
            ProjectID: projectId,
            ModelIDs: selectedModelIds,
        };

        try {
            const documentResponse = await Axios.post('/projects/printmodelslist', data);
            setDocVersion(documentResponse);
        } catch (error) {
            Modal.error({
                title: error.message || 'Error generating document',
            });
            console.error('Create Project Models List Error => ', error);
        } finally {
            setIsFillingTemplate(false);
        }
    };

    return (
        <>
            <NwDrawer width="70vw" title="Print Project Models List" onClose={onClose}>
                {isFetchingProject ? (
                    <Loading textBlack />
                ) : (
                    <ReportCreationDrawerWrapper>
                        <div className="export-content">
                            {projectEvents.length > 0 && (
                                <>
                                    <NwDivider>Events</NwDivider>
                                    <div className="events-container">
                                        {projectEvents.map((event, eventIndex) => (
                                            <div className="event-container" key={`project-event-${event.ID}`}>
                                                <ProjectPrintEventItem
                                                    checked={event.active}
                                                    event={event}
                                                    onChange={handleChangeEvent(eventIndex)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            <ModelSelectorContainer className="project-models-list-selector">
                                <h1>Models in selected events</h1>
                                <ModelsConfirmedInfo />
                                <SelectionBar actions={modelSelectionActions} />
                                {visibleModels.length > 0 ? (
                                    visibleModels.map((model) => (
                                        <div className="model-wrapper" key={model.ModelID}>
                                            <Switch
                                                checked={model.Active}
                                                onChange={handleChangeModel(model.ModelID)}
                                                text={<ModelName model={model} />}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <Alert
                                        message="No models available for the selected events"
                                        type="warning"
                                        showIcon
                                    />
                                )}
                            </ModelSelectorContainer>
                        </div>
                        <div style={{ textAlign: 'center', margin: '1rem' }}>
                            {selectedModelIds.length > 0 ? (
                                <ButtonTemplateSelector
                                    loading={isFillingTemplate}
                                    templateArea={PRINT_AREA}
                                    onClick={handleGenerateModelsList}
                                />
                            ) : (
                                <Alert
                                    message="Select at least one model to generate the document"
                                    type="warning"
                                    showIcon
                                />
                            )}
                        </div>
                    </ReportCreationDrawerWrapper>
                )}
            </NwDrawer>
            {docVersion && (
                <NwDrawer
                    onClose={() => setDocVersion(null)}
                    padding="0px"
                    title="Print Project Models List"
                    visible={true}
                    whiteBg={false}
                    width="80vw"
                    maxWidth={'1024px'}
                >
                    <PdfPrintDrawer docVersion={docVersion} />
                </NwDrawer>
            )}
        </>
    );
};

export default ProjectModelsListDrawer;
